// Everything this lib says to the identity server (ADR 0003, "The handshake — a
// person"): the token endpoint, sign-out, and what a person does with a login
// no account owns yet. Every call that the browser's key must stand behind
// carries a fresh proof from it.
//
// Like every module in sdk/, this holds no environment of its own: the host
// supplies where the identity server is and which app this is, the same way it
// supplies every backend URL.
import { createProof, getKeyThumbprint } from './proofKey.js';
import { clearSession, getSession, setSession } from './sessionTokenStorage.js';

// Renew this long before the token runs out, so a request made now does not
// arrive carrying one that expired on the way.
const RENEWAL_SKEW_MS = 30_000;

const identityServer = {
    baseUrl: null,
    appId: null
};

// One renewal at a time: a burst of calls with an expiring token is one refresh.
let renewing = null;

export function configureIdentityServer({ baseUrl, appId } = {}) {
    if (baseUrl !== undefined) identityServer.baseUrl = baseUrl;
    if (appId !== undefined) identityServer.appId = appId;
    return { ...identityServer };
}

function requireConfiguration() {
    if (!identityServer.baseUrl || !identityServer.appId) {
        throw new Error('The identity server is not configured — pass `identityServer` to configureBackends first.');
    }
    return identityServer;
}

function refusal(response, body) {
    const error = new Error(`The identity server answered ${response.status}`);
    error.statusCode = response.status;
    error.body = body;
    return error;
}

async function readJson(response) {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

// The session a call on the person's behalf presents, or a 401 when there is none.
async function requireSession(what) {
    const session = await currentSession();
    if (session === null) {
        const error = new Error(`No session to ${what}.`);
        error.statusCode = 401;
        throw error;
    }
    return session;
}

// One POST to the identity server with a proof for exactly that URL. `headers`
// and `body` say which shape the endpoint reads: a form for /token, JSON elsewhere.
// An https page cannot fetch an http identity server (mixed content), and a phone
// cannot reach the laptop's localhost at all. So an https dev page sends every
// identity request — the authorize and logout navigations too — to the dev
// server's same-origin `/identity` proxy (see `identityUrl`). The proof still
// names the identity server's own URL: that is what the laptop accepts. A
// deployed https identity server, and any http page, is reached at baseUrl.
const IDENTITY_DEV_PROXY_PREFIX = '/identity';

function isPrivateIpv4(hostname) {
    const octets = String(hostname).split('.');
    if (octets.length !== 4 || !octets.every((octet) => /^(0|[1-9]\d{0,2})$/.test(octet) && Number(octet) <= 255)) {
        return false;
    }
    const [first, second] = octets.map(Number);
    if (first === 10) {
        return true;
    }
    if (first === 172 && second >= 16 && second <= 31) {
        return true;
    }
    return first === 192 && second === 168;
}

// A host only a development machine answers for: the loopback names, and the
// three private IPv4 ranges a laptop takes on a LAN.
function isDevelopmentHost(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || isPrivateIpv4(hostname);
}

// The loopback name every development server answers on, and the only host a
// laptop's SERVICE_PUBLIC_ORIGINS carries. See `sameOriginDevProofUrl`.
const LOOPBACK_HOST = 'localhost';

// The hosts a page is served on when it runs on the same machine as the servers
// it calls. Such a page reaches every one of them directly, so none of the work
// below applies to it. See `devProxyTarget`.
const LOOPBACK_PAGE_HOSTS = Object.freeze(['localhost', '127.0.0.1']);

// The scheme a request takes once it goes through the https page's own origin.
const PAGE_SCHEME_FOR = Object.freeze({ 'http:': 'https:', 'ws:': 'wss:' });

/**
 * The request a phone cannot send as it stands, parsed — or null when there is
 * nothing to work around.
 *
 * One predicate for every answer below, because they must agree on exactly
 * which requests go through the page origin. Null for a relative URL (already
 * same-origin), an https or wss URL, any public host, and every request made
 * from an http page. A ws URL counts: a socket's handshake is an http request,
 * and an https page refuses it the same way.
 *
 * Null for a page on `localhost` or `127.0.0.1` as well, whatever its scheme:
 * that page runs on the same machine as the servers, reaches every one of them
 * directly, and is a secure context on http already. A laptop is this case, and
 * its `npm run dev` serves http://localhost:5173; the opt-in
 * https://localhost:5173 (MOBILE_DEV_HTTPS=1) must not be proxied either,
 * because it goes direct too. The page's own host is read from `origin`.
 */
function devProxyTarget(url) {
    if (globalThis.location?.protocol !== 'https:' || !globalThis.location?.origin) {
        return null;
    }
    let target;
    let page;
    try {
        target = new URL(url);
        page = new URL(globalThis.location.origin);
    } catch {
        return null;
    }
    if (LOOPBACK_PAGE_HOSTS.includes(page.hostname)) {
        return null;
    }
    if (!PAGE_SCHEME_FOR[target.protocol] || !isDevelopmentHost(target.hostname)) {
        return null;
    }
    return target;
}

// The page's own origin, in the scheme `target` needs: https for a request, wss
// for a socket.
function pageOriginFor(target) {
    return `${PAGE_SCHEME_FOR[target.protocol]}//${new URL(globalThis.location.origin).host}`;
}

/**
 * The URL to fetch for a request, which on a phone is not the URL the request
 * is about.
 *
 * The same mixed-content wall `identityUrl` works around, one layer
 * out. On a phone the page is https://<lan>:5173 and a backend's configured
 * base URL is still http://localhost:4500/api — the laptop's own port, which
 * the phone cannot reach and the browser would block anyway. The browser
 * drops the request, the server logs nothing, and the caller sees a network
 * failure with no status: that is how a signed-in session came back from
 * `GET /session/me` as "not authenticated" and sent the dealer back out to the
 * hosted login forever.
 *
 * So an https page sends that request to its own origin instead, keeping the
 * path and the query. Vite proxies `/api` to http://localhost:4500 and keeps
 * the path, so the same request arrives at the same endpoint.
 *
 * The credential is made for `sameOriginDevProofUrl(url)`, never for this: a
 * DPoP proof's `htu` names an origin the server itself answers on, and a proof
 * made for https://<lan>:5173/api/... matches nothing the server will accept.
 *
 * Everything else is returned untouched — see `devProxyTarget`.
 */
export function sameOriginDevUrl(url) {
    const target = devProxyTarget(url);
    if (target === null) {
        return url;
    }
    return `${pageOriginFor(target)}${target.pathname}${target.search}`;
}

/**
 * The URL the credential for a proxied request must name, which on a phone is
 * not the URL the host configured either.
 *
 * `sameOriginDevUrl` moves the fetch; this moves the proof. vibe-server names
 * the origins it answers on in SERVICE_PUBLIC_ORIGINS, and a laptop's value is
 * `http://localhost:4500,http://localhost:5173` — loopback names, because the
 * LAN IP changes and the container reads that list once at boot. The iOS
 * live-reload script used to rewrite every localhost host in the portal's
 * gitignored manifest to the LAN IP, so the configured base became
 * http://10.0.0.128:4500/api and the proof named an origin the server does not
 * list: DPOP_PROOF_URL_MISMATCH, a 401 on `GET /session/me`, a null user, and a
 * dealer sent back to the hosted login about once a second.
 *
 * So a request that goes through the page origin proves the loopback name of
 * the same port. The port and the path are the server's own and are kept; only
 * the host the phone had to be told goes back to what the server lists.
 *
 * A configured localhost or 127.0.0.1 base is returned untouched: that is a
 * laptop's own manifest, and it already names what the server accepts. The
 * identity server's token POST does not come through here — it builds its proof
 * from the configured base URL in `post`, and the identity server accepts the
 * LAN name beside the loopback one.
 */
export function sameOriginDevProofUrl(url) {
    const target = devProxyTarget(url);
    if (target === null || !isPrivateIpv4(target.hostname)) {
        return url;
    }
    const proofTarget = new URL(target);
    proofTarget.hostname = LOOPBACK_HOST;
    return proofTarget.href;
}

/**
 * The path prefix the dev server proxies to a laptop port: a request to
 * `/development-port-proxy/<port>/<path>` arrives at http://localhost:<port>/<path>.
 */
export const DEVELOPMENT_PORT_PROXY_PREFIX = '/development-port-proxy';

const DEVELOPMENT_PORT_PROXY_PATH = /^\/development-port-proxy\/(\d+)(\/.*)?$/;

/**
 * `sameOriginDevUrl` for a server whose routes sit at the root.
 *
 * vibe-server answers under `/api`, so its requests can keep their path on the
 * page origin and the dev server still knows where to send them. The event
 * runtime's servers (bidding :4030, the delta socket :9001) answer at the
 * root, so a bare `/item-biddings` on the page origin names
 * nothing the dev server can route. The port goes into the path instead, under
 * `DEVELOPMENT_PORT_PROXY_PREFIX`, and the dev server strips it on the way out.
 *
 * Without this an https page on a phone cannot reach the runtime at all: the
 * browser blocks http://<lan>:4030 as mixed content, the server logs nothing,
 * and the Live page says nothing is live while every sale is running.
 *
 * The credential is made for `developmentPortProofUrl` of the result.
 */
export function sameOriginDevPortUrl(url) {
    const target = devProxyTarget(url);
    if (target === null || !target.port) {
        return url;
    }
    // A base URL with no path parses to the path `/`; keeping it would make
    // every request the SDK appends a path to start with `//`.
    const path = target.pathname === '/' && !/^[a-z]+:\/\/[^/]+\//i.test(url) ? '' : target.pathname;
    return `${pageOriginFor(target)}${DEVELOPMENT_PORT_PROXY_PREFIX}/${target.port}${path}${target.search}`;
}

/**
 * The URL a proof for a `sameOriginDevPortUrl` request must name: the server's
 * own loopback URL, the one its SERVICE_PUBLIC_ORIGINS lists (see
 * `sameOriginDevProofUrl`). A transport hands its credential function the URL it
 * is about to send, which for these is the proxied one, so this reads the port
 * back out of the path. Any other URL is returned untouched.
 */
export function developmentPortProofUrl(url) {
    if (!globalThis.location?.origin) {
        return url;
    }
    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }
    const match = DEVELOPMENT_PORT_PROXY_PATH.exec(parsed.pathname);
    if (parsed.host !== new URL(globalThis.location.origin).host || match === null) {
        return url;
    }
    const scheme = parsed.protocol === 'wss:' ? 'ws:' : 'http:';
    return `${scheme}//${LOOPBACK_HOST}:${match[1]}${match[2] ?? '/'}${parsed.search}`;
}

// Where the browser goes for an identity server path. An https dev page off
// loopback — the phone on https://<lan>:5173 — reaches an http identity server on
// a development host through its own `/identity` proxy, so the manifest keeps
// naming http://localhost:4200 and no LAN address is ever written into it. Every
// other identity server, the laptop's own included, is reached directly.
function identityUrl(baseUrl, path) {
    if (devProxyTarget(baseUrl) === null) {
        return `${baseUrl}${path}`;
    }
    return `${globalThis.location.origin}${IDENTITY_DEV_PROXY_PREFIX}${path}`;
}

async function post(path, { headers, body, accessToken }) {
    const { baseUrl } = requireConfiguration();
    const proofUrl = `${baseUrl}${path}`;
    const proofHeaders = { DPoP: await createProof({ method: 'POST', url: proofUrl, accessToken }) };
    if (accessToken) {
        proofHeaders.Authorization = `DPoP ${accessToken}`;
    }
    const response = await fetch(identityUrl(baseUrl, path), {
        method: 'POST',
        headers: { ...headers, ...proofHeaders },
        body
    });
    const answer = await readJson(response);
    if (!response.ok) {
        throw refusal(response, answer);
    }
    return answer;
}

function postForm(path, fields) {
    return post(path, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fields)
    });
}

function postJson(path, payload, { accessToken } = {}) {
    return post(path, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        accessToken
    });
}

/** Where to send the person to sign in. The key's thumbprint rides along, so the code that comes back works for this browser only. */
export async function authorizeUrl({ redirectUri, state, identityProvider, prompt, intent }) {
    const { baseUrl, appId } = requireConfiguration();
    const params = new URLSearchParams({
        client_id: appId,
        response_type: 'code',
        redirect_uri: redirectUri,
        state,
        dpop_jkt: await getKeyThumbprint()
    });
    if (identityProvider) params.set('identity_provider', identityProvider);
    if (prompt) params.set('prompt', prompt);
    if (intent) params.set('intent', intent);
    return `${identityUrl(baseUrl, '/authorize')}?${params.toString()}`;
}

/** Where to send the person to end the hosted login's own session, returning to `returnTo`. */
export function logoutUrl(returnTo) {
    const { baseUrl, appId } = requireConfiguration();
    const params = new URLSearchParams({ client_id: appId, post_logout_redirect_uri: returnTo });
    return `${identityUrl(baseUrl, '/logout')}?${params.toString()}`;
}

/**
 * Trades the one-time code. The answer always carries an `access_token`. For a
 * sign-in that found its Human it is a session's, with the refresh token that
 * renews it. For one that did not, the answer says `account_pending`: the token
 * is for the account endpoints only, and nothing renews it.
 */
export function exchangeCode({ code, redirectUri }) {
    const { appId } = requireConfiguration();
    return postForm('/token', { grant_type: 'authorization_code', code, client_id: appId, redirect_uri: redirectUri });
}

async function renew(refreshToken) {
    const { appId } = requireConfiguration();
    try {
        const tokens = await postForm('/token', { grant_type: 'refresh_token', refresh_token: refreshToken, client_id: appId });
        return await setSession(tokens);
    } catch (error) {
        // A refused renewal ends the session; an outage does not.
        if (error.statusCode) {
            await clearSession();
            return null;
        }
        throw error;
    }
}

/**
 * The session to present right now: the stored one while its token has life
 * left, a renewed one when it is about to run out, null when there is none or
 * it cannot be renewed any more.
 */
export async function currentSession() {
    const session = await getSession();
    if (session === null) {
        return null;
    }
    if (session.expiresAt - RENEWAL_SKEW_MS > Date.now()) {
        return session;
    }
    if (renewing === null) {
        renewing = renew(session.refreshToken).finally(() => {
            renewing = null;
        });
    }
    return renewing;
}

/**
 * Sign out this device, the server's half: spends this browser's refresh token,
 * forgets its key, and closes its live connections. The person's other devices
 * are untouched.
 */
export async function revokeSession() {
    const session = await getSession();
    if (session === null) {
        return;
    }
    await postJson('/revoke', { refresh_token: session.refreshToken });
}

/**
 * Sign out everywhere, the server's half: ends every session the person has,
 * on every device, this one included. Throws when it could not be done — the
 * person must not believe their other devices are signed out when they are not.
 */
export async function revokeEverywhere() {
    const session = await requireSession('sign out everywhere from');
    await postJson('/revoke/everywhere', {}, { accessToken: session.accessToken });
}

/** Sign out one of the person's other devices, named by the `id` its row in `listSessions` carries. */
export async function revokeDevice(deviceId) {
    const session = await requireSession('sign out a device from');
    await postJson('/revoke/device', { device: deviceId }, { accessToken: session.accessToken });
}

/**
 * The person's sessions, one per device, this one first: `{ id, appId,
 * signInMethod, userAgent, ipAddress, signedInAt, lastActiveAt, isThisDevice }`.
 */
export async function listSessions() {
    const session = await requireSession('list the sessions of');
    return (await getJson('/sessions', { accessToken: session.accessToken })).sessions;
}

// One GET to the identity server, presenting the session's token and a proof for exactly that URL.
async function getJson(path, { accessToken }) {
    const { baseUrl } = requireConfiguration();
    const url = `${baseUrl}${path}`;
    // The proof names the identity server's own URL; on a phone the fetch goes
    // through the dev server's same-origin `/identity` proxy, as `post` does.
    const response = await fetch(identityUrl(baseUrl, path), {
        method: 'GET',
        headers: {
            Authorization: `DPoP ${accessToken}`,
            DPoP: await createProof({ method: 'GET', url, accessToken })
        }
    });
    const answer = await readJson(response);
    if (!response.ok) {
        throw refusal(response, answer);
    }
    return answer;
}

/**
 * The person's second factor: `{ available, enabled }`. `available` is false when this sign-in
 * is Google or Microsoft — the provider's own multi-factor covers it. Throws a 409
 * `SIGN_IN_AGAIN` when this device signed in before the identity server kept what it needs, or
 * Cognito no longer honours it: the person signs in again and tries once more.
 */
export async function mfaStatus() {
    const session = await requireSession('read the second factor of');
    return getJson('/accounts/mfa', { accessToken: session.accessToken });
}

/** Starts setting up the authenticator app: `{ secret, otpauthUri }` — what to scan, and the same as text. Nothing is on yet. */
export async function beginMfa() {
    const session = await requireSession('set up a second factor for');
    return postJson('/accounts/mfa/begin', {}, { accessToken: session.accessToken });
}

/** Finishes set-up with the six digits the authenticator app shows. Throws a 400 `MFA_CODE_WRONG` for a wrong code. */
export async function enableMfa(code) {
    const session = await requireSession('set up a second factor for');
    return postJson('/accounts/mfa/enable', { code }, { accessToken: session.accessToken });
}

/** Turns the authenticator app off: `{ enabled: false }`. */
export async function disableMfa() {
    const session = await requireSession('turn off the second factor of');
    return postJson('/accounts/mfa/disable', {}, { accessToken: session.accessToken });
}

// What a person does with a login no account owns yet. The first three present
// the pending-account token the way any request presents a session's — the
// token, and a proof for this request — and each answers with the session that
// replaces it. Attach acts for the person already signed in, on a pending
// login this browser's key holds, named by its id.
export function createAccount(pendingAccessToken) {
    return postJson('/accounts/create', {}, { accessToken: pendingAccessToken });
}

export function restoreAccount(pendingAccessToken) {
    return postJson('/accounts/restore', {}, { accessToken: pendingAccessToken });
}

export function connectMatch(pendingAccessToken) {
    return postJson('/accounts/connect', {}, { accessToken: pendingAccessToken });
}

export async function attachLogin(pendingLoginId) {
    const session = await currentSession();
    if (session === null) {
        const error = new Error('No session to attach a login to.');
        error.statusCode = 401;
        throw error;
    }
    return postJson('/accounts/attach', { pending_login: pendingLoginId }, { accessToken: session.accessToken });
}

export const identityServerClient = {
    authorizeUrl,
    logoutUrl,
    exchangeCode,
    currentSession,
    revokeSession,
    revokeEverywhere,
    revokeDevice,
    listSessions,
    mfaStatus,
    beginMfa,
    enableMfa,
    disableMfa,
    createAccount,
    restoreAccount,
    connectMatch,
    attachLogin
};
export default identityServerClient;
